<template>
  <div class="content d-flex flex-column flex-column-fluid" id="kt_content">
    <div class="toolbar" id="kt_toolbar">
      <app-sub-view-header :title="title" :breadcrumbs="breadcrumbs" :back="false"></app-sub-view-header>
    </div>
    <div v-if="isReady" class="post d-flex flex-column-fluid" id="kt_post">
      <div id="kt_content_container" class="container-xxl">
        <div class="card mb-4">
          <div class="card-body py-4 px-6">
            <div class="d-flex align-items-center justify-content-between flex-wrap flex-lg-nowrap gap-3">

              <!-- Icône + nom + statut + infos (prend la place restante, l'adresse
                   longue est tronquée pour ne pas renvoyer les boutons à la ligne) -->
              <div class="d-flex align-items-center gap-3 flex-grow-1" style="min-width:0">
                <div class="symbol symbol-45px">
                  <span class="symbol-label bg-light-primary">
                    <i class="fas fa-shopping-basket fs-3 text-primary"></i>
                  </span>
                </div>
                <div style="min-width:0">
                  <div class="d-flex align-items-center gap-2">
                    <span class="text-gray-900 fs-4 fw-bolder">{{ shop.name }}</span>
                    <span v-if="shop.is_open === false" class="badge badge-light-danger">
                      <i class="fas fa-lock fs-9 me-1"></i>{{ $t('shop.closed') }}
                    </span>
                    <span v-else class="badge badge-light-success">
                      <i class="fas fa-lock-open fs-9 me-1"></i>{{ $t('shop.open') }}
                    </span>
                  </div>
                  <div class="d-flex gap-4 mt-1" style="min-width:0">
                    <span class="text-gray-400 fs-7 text-nowrap">
                      <i class="bi bi-telephone me-1"></i>{{ shop.phone_code && !String(shop.phone || "").startsWith("+") ? shop.phone_code + " " : "" }}{{ shop.phone }}
                    </span>
                    <span class="text-gray-400 fs-7 text-truncate" :title="(shop.country ? shop.country.name + ', ' : '') + (shop.address || '')">
                      <i class="bi bi-geo-alt me-1"></i>{{ shop.country ? shop.country.name + ', ' : '' }}{{ shop.address }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Boutons d'action -->
              <div class="d-flex gap-2 flex-wrap flex-lg-nowrap flex-lg-shrink-0">
                <a v-if="shop.id && $hasShopModule('ecommerce')" :href="ecommerceUrl" target="_blank" class="btn btn-sm btn-light-info">
                  <i class="fas fa-store me-1"></i>{{ $t('shop.view_ecommerce') }}
                </a>
                <button v-if="shop.id && $hasShopModule('ecommerce')" type="button" class="btn btn-sm btn-light-primary"
                  data-bs-toggle="modal" data-bs-target="#qrCodeModal">
                  <i class="fas fa-qrcode me-1"></i>{{ $t('shop.qr_code') }}
                </button>
                <button v-if="shop.id && $can('boutique_update')" @click="openToggleOpenModal" class="btn btn-sm"
                  :class="shop.is_open !== false ? 'btn-light-danger' : 'btn-light-success'">
                  <i :class="shop.is_open !== false ? 'fas fa-lock me-1' : 'fas fa-lock-open me-1'"></i>
                  {{ shop.is_open !== false ? $t('shop.close_shop') : $t('shop.open_shop') }}
                </button>
                <a v-if="$can('boutique_update')" @click="openEditModal" class="btn btn-sm btn-primary">{{ $t('shop.edit') }}</a>
                <a v-show="shop?.archive" @click="unArchiveData" class="btn btn-sm btn-light">{{ $t('shop.restore') }}</a>
                <a v-if="$can('boutique_delete')" v-show="shop?.archive === false && shopProductCount === 0 && shopUsersCount === 0 && !shop?.stores?.length" @click.prevent="deleteData" class="btn btn-sm btn-danger">{{ $t('shop.delete') }}</a>
              </div>

            </div>
          </div>
        </div>

        <!-- Layout : menu gauche + contenu droite -->
        <div class="row g-5 mt-1">

          <!-- Menu vertical gauche -->
          <div class="col-lg-3 col-md-3">
            <div class="card card-flush">
              <div class="card-body p-3">
                <div class="menu menu-column menu-rounded menu-gray-600 menu-hover-bg menu-active-bg fw-semibold fs-6" @click="scrollToContent">

                  <!-- Un groupe par grand thème ; ses pages sont les onglets au-dessus du contenu -->
                  <div v-for="group in menuGroups" :key="group.key" class="menu-item mb-1">
                    <router-link class="menu-link px-3 py-2 rounded" :to="routePath(group.items[0].route)"
                      :class="activeGroup && activeGroup.key === group.key ? 'active bg-light-primary text-primary' : ''">
                      <span class="shop-menu-icon icon-primary position-relative"><i :class="group.icon"></i>
                        <!-- point rouge : une page du groupe demande une action (rupture, transfert, crédit en retard) -->
                        <span v-if="group.items.some(i => i.alert > 0)" class="shop-alert-dot shop-alert-dot--corner"></span>
                      </span>
                      <span class="menu-title d-flex align-items-center justify-content-between w-100">
                        {{ $t(group.label) }}
                        <span>
                          <span v-for="item in group.items.filter(i => i.badge > 0)" :key="item.route" class="badge ms-1" :class="item.badgeClass">{{ item.badge }}</span>
                        </span>
                      </span>
                    </router-link>
                  </div>

                </div>
              </div>
            </div>
          </div>

          <!-- Contenu droite -->
          <div ref="shopContent" class="col-lg-9 col-md-9 shop-content">
            <!-- Onglets du groupe ouvert (Stock : Entrées, Transferts, Inventaires, Ajustements…) -->
            <div v-if="activeGroup && activeGroup.items.length > 1" class="card card-flush mb-5">
              <div class="card-body py-0 px-5">
                <ul class="nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-6 fw-bold flex-nowrap" style="overflow-x: auto; overflow-y: hidden;">
                  <li v-for="item in activeGroup.items" :key="item.route" class="nav-item">
                    <router-link class="nav-link text-active-primary py-4 me-6 text-nowrap" :to="routePath(item.route)"
                      :class="{ active: item.active.includes($route.name) }">
                      {{ $t(item.label) }}
                      <span v-if="item.alert > 0" class="shop-alert-dot ms-2"></span>
                      <span v-if="item.badge > 0" class="badge ms-2" :class="item.badgeClass">{{ item.badge }}</span>
                    </router-link>
                  </li>
                </ul>
              </div>
            </div>
            <router-view @reloadEditData="initEditData" :shop="shop" :entryCount="shopEntryCount" :transferCount="shopTransferCount"></router-view>
          </div>

        </div>

      </div>

    </div>
    <div class="container-xxl" v-show="!isReady">
      <Loader />
    </div>

    <div class="modal fade" id="editModal" data-backdrop="static" tabindex="-1" role="dialog"
      aria-labelledby="staticBackdrop" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <form @submit.prevent="updateShopData" class="form">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ $t('shop.edit_shop') }}</h5>


              <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" data-bs-dismiss="modal" aria-label="Close">

                <span class="svg-icon svg-icon-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)"
                      fill="currentColor" />
                    <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)"
                      fill="currentColor" />
                  </svg>
                </span>

              </div>


            </div>
            <div class="modal-body">
              <div class="row">
                <div class="col-12 mb-2">
                  <div class="d-flex flex-column fv-row">
                    <!--begin::Label-->
                    <label class="d-flex align-items-center fs-6 fw-bold mb-2">
                      <span class="required">{{ $t('shop.name') }}</span>
                    </label>
                    <!--end::Label-->
                    <input class="form-control form-control-solid" placeholder="" v-model="formEditData.name" required />
                  </div>
                </div>
                <div class="col-12 my-1">
                  <div class="d-flex flex-column fv-row">
                    <!--begin::Label-->
                    <label class="d-flex align-items-center fs-6 fw-bold mb-2">
                      <span class="required">{{ $t('shop.address') }}</span>
                    </label>
                    <!--end::Label-->
                    <input class="form-control form-control-solid " :placeholder="$t('shop.address_placeholder')"
                           v-model="formEditData.address" required />
                  </div>
                </div>
                <InputTel
                    name="editShopPhone"
                    type="text"
                    :label="$t('shop.phone')"
                    containerClass="col-12 my-1"
                    labelClass="fs-6 fw-bold mb-2"
                    :inputClass="'form-control form-control-solid'"
                    v-model:phone="formEditData.phone"
                    v-model:phone-code="formEditData.phoneCode"
                    :placeholder="$t('enter_phone')"
                    :isRequired="true" />
<!--                <div class="col-12 my-1">-->
<!--                  <div class="d-flex flex-column mb-4 fv-row">-->
<!--                    &lt;!&ndash;begin::Label&ndash;&gt;-->
<!--                    <label class="d-flex align-items-center fs-6 fw-bold mb-2">-->
<!--                      <span class="required">{{ $t('shop.phone') }}</span>-->
<!--                    </label>-->
<!--                    &lt;!&ndash;end::Label&ndash;&gt;-->
<!--                    <vue-tel-input -->
<!--                                  @country-changed="thirstInputCountryChanged" -->
<!--                                  :inputOptions="options"-->
<!--                                  @input="thirstInputChanged"-->
<!--                                  :dropdownOptions="dropdownOptions" -->
<!--                                  :autoDefaultCountry="true" -->
<!--                                  id="shop_phone"-->
<!--                                  class="form-control form-control-solid"-->
<!--                                  name="shop_phone"-->
<!--                                  v-model="phoneNumber">-->
<!--                    </vue-tel-input>-->
<!--                  </div>-->
<!--                </div>-->
<!--                <div class="col-12 my-1">-->
<!--                  <div class="d-flex flex-column fv-row mb-4">-->
<!--                    &lt;!&ndash;begin::Label&ndash;&gt;-->
<!--                    <label class="fs-6 fw-bold">-->
<!--                      <span class="">{{ $t('shop.stores') }}</span>-->
<!--                    </label>-->
<!--                    &lt;!&ndash;end::Label&ndash;&gt;-->
<!--                    <div class="d-flex align-items-stretch h-100">-->
<!--                      <div class="flex-grow-1">-->
<!--                        <select name="stores[]" v-model="formEditData.stores" data-control="select2"-->
<!--                                data-dropdown-parent="#editModal" data-search="true"-->
<!--                                multiple="multiple"-->
<!--                                :data-placeholder="$t('shop.stores_placeholder')" class="form-select  kt_select2 form-control form-control-solid">-->
<!--                          <option v-for="store in getStores" :key="store.id" :value="store.id">{{ store.name }}-->
<!--                          </option>-->
<!--                        </select>-->
<!--                      </div>-->
<!--                      <div data-bs-target="#createStoreModal" data-bs-toggle="modal"-->
<!--                           class="btn btn-light d-flex align-items-center justify-content-center border border-start-0" id="basic-addon2">-->
<!--                        <i class="fas fa-plus fs-4"></i>-->
<!--                      </div>-->
<!--                    </div>-->
<!--                  </div>-->
<!--                </div>-->
                <div class="col-6 d-flex align-items-stretch my-1">
                  <div class="d-flex flex-column mt-auto fv-row">
                    <!--begin::Label-->
                    <label class="d-flex align-items-center fs-6 fw-bold">
                      <span class="">{{ $t('shop.prefix') }} </span>
                    </label>
                    <!--end::Label-->
                    <input class="form-control form-control-solid " :placeholder="$t('shop.prefix_placeholder')"
                           v-model="formEditData.prefix"/>
                  </div>
                </div>
<!--                <div class="col-4 d-flex align-items-stretch my-1">-->
<!--                  <div class="d-flex flex-column mt-auto fv-row">-->
<!--                    &lt;!&ndash;begin::Label&ndash;&gt;-->
<!--                    <label class="d-flex align-items-center fs-6 fw-bold">-->
<!--                      <span class="">{{ $t('shop.transfer_prefix') }} </span>-->
<!--                    </label>-->
<!--                    &lt;!&ndash;end::Label&ndash;&gt;-->
<!--                    <input class="form-control form-control-solid " :placeholder="$t('shop.transfer_prefix_placeholder')"-->
<!--                           v-model="formEditData.transfer_number"/>-->
<!--                  </div>-->
<!--                </div>-->
                <div class="col-6 d-flex align-items-stretch my-1">
                  <div class="d-flex flex-column mt-auto fv-row">
                    <!--begin::Label-->
                    <label class="d-flex align-items-center  fs-6 fw-bold">
                      <span class="">{{ $t('shop.invoice_prefix') }} </span>
                    </label>
                    <!--end::Label-->
                    <input class="form-control form-control-solid " :placeholder="$t('shop.invoice_number_placeholder')"
                           v-model="formEditData.invoice_number"/>
                  </div>
                </div>

<!--                <div class="col-12 my-1">-->
<!--                  <label class="d-flex align-items-center fs-6 fw-bold">-->
<!--                    <span class="">{{ $t('shop.status') }}</span>-->
<!--                  </label>-->
<!--                  &lt;!&ndash;end::Label&ndash;&gt;-->
<!--                  <select class="form-select  kt_select2 form-control form-control-solid" name="active" v-model="formEditData.archive"-->
<!--                          data-control="select2" data-dropdown-parent="#editModal"-->
<!--                          data-placeholder="Select an option">-->
<!--                    <option></option>-->
<!--                    <option :value="false">{{ $t('shop.active') }}</option>-->
<!--                    <option :value="true">{{ $t('shop.inactive') }}</option>-->
<!--                  </select>-->
<!--                </div>-->
              </div>
              <!-- Coordonnées et identité (logo, favicon, adresse complète...) -->
              <div class="separator separator-dashed my-6"></div>
              <h5 class="fw-bolder mb-4">Coordonnées et identité</h5>
              <ShopBranding v-if="shop.id" ref="branding" :shop="shop" embedded />
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-sm btn-light btn-light-close" data-bs-dismiss="modal">{{ $t('button.close') }}</button>
              <button type="submit" class="btn btn-sm btn-primary" :disabled="isLoading">
                <span class="indicator-label" v-if="!isLoading">{{ $t('button.save') }}</span>
                <span class="" v-else>{{ $t('loading') }}
                  <span class="spinner-border spinner-border-sm align-middle ms-2"></span></span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal fermeture boutique -->
    <div class="modal fade" id="toggleOpenModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-lock me-2 text-danger"></i>
              {{ $t('shop.close_shop_title') }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted fs-6 mb-4">{{ $t('shop.close_shop_desc') }}</p>
            <div class="fv-row">
              <label class="form-label fw-bold">{{ $t('shop.closed_message_label') }}</label>
              <textarea v-model="closedMessage" class="form-control form-control-solid" rows="3"
                        :placeholder="$t('shop.closed_message_placeholder')"></textarea>
              <span class="text-muted fs-8 mt-1 d-block">{{ $t('shop.closed_message_hint') }}</span>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light btn-light-close" data-bs-dismiss="modal">{{ $t('button.cancel') }}</button>
            <button type="button" class="btn btn-danger" @click="confirmToggleOpen" :disabled="isToggling">
              <span v-if="isToggling" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="fas fa-lock me-1"></i>
              {{ $t('shop.close_shop') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="modal fade" id="qrCodeModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-qrcode me-2 text-primary"></i>
              {{ $t('shop.qr_code') }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body text-center">
            <p class="text-muted fs-6 mb-5">{{ $t('shop.qr_code_desc') }}</p>
            <QRCodeVue3
              v-if="shop.id"
              :width="220"
              :height="220"
              :value="ecommerceUrl"
              :qrOptions="{ typeNumber: 0, mode: 'Byte', errorCorrectionLevel: 'H' }"
              :imageOptions="{ hideBackgroundDots: true, imageSize: 0.4, margin: 0 }"
              :dotsOptions="{ type: 'square', color: '#000000' }"
              fileExt="png"
              :download="true"
              downloadButton="btn btn-sm btn-primary mt-5"
              :buttonName="$t('shop.qr_code_download')"
              :downloadOptions="{ name: 'qr-' + shop.name, extension: 'png' }"
            />
            <div class="text-muted fs-8 mt-3">{{ ecommerceUrl }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="modal fade" tabindex="-1" id="createStoreModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('store.create.title') }}</h5>
            <!--begin::Close-->
            <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" data-bs-target="#editModal" data-bs-toggle="modal" aria-label="Close">
              <!--begin::Svg Icon | path: icons/duotune/arrows/arr061.svg-->
              <span class="svg-icon svg-icon-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)"
                        fill="currentColor" />
                  <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)"
                        fill="currentColor" />
                </svg>
              </span>
              <!--end::Svg Icon-->
            </div>

            <!--end::Close-->
          </div>
          <div class="modal-body">
            <!--begin:Form-->
            <form @submit.prevent="createStoreData" id="kt_modal_new_target_form" class="form">

              <div class="d-flex flex-column mb-8 fv-row">
                <!--begin::Label-->
                <label class="d-flex align-items-center fs-6 fw-bold mb-2">
                  <span class="required">{{ $t('store.form.name') }}</span>
                  <i class="fas fa-exclamation-circle ms-2 fs-7" data-bs-toggle="tooltip"
                     title="Specify a target name for future usage and reference"></i>
                </label>
                <!--end::Label-->
                <input type="text" class="form-control form-control-solid" v-model="formStore.name"
                       :placeholder="$t('store.form.name')" required />
              </div>

              <div class="d-flex flex-column mb-8 fv-row">
                <label class="d-flex align-items-center fs-6 fw-bold mb-2">
                  <span class="required">{{ $t('store.form.address') }}</span>
                  <i class="fas fa-exclamation-circle ms-2 fs-7" data-bs-toggle="tooltip"
                     title="Veuillez bien renseigner ce champ"></i>
                </label>
                <input type="text" class="form-control form-control-solid" v-model="formStore.address"
                       :placeholder="$t('store.form.address')" required />
              </div>
              <InputTel
                  name="newShopStorePhone"
                  type="text"
                  :label="$t('store.form.telephone')"
                  containerClass="col-12 my-1"
                  labelClass="fs-6 fw-bold mb-2"
                  :inputClass="'form-control form-control-solid'"
                  v-model:phone="formStore.phone"
                  v-model:phone-code="formStore.phoneCode"
                  :placeholder="$t('enter_phone')"
                  :isRequired="true" />

              <div class="d-flex flex-column mb-8 fv-row">
                <label class="d-flex align-items-center fs-6 fw-bold mb-2">
                  <span class="required">{{ $t('store.form.prefixe') }}</span>
                </label>
                <input type="text" required class="form-control form-control-solid" v-model="formStore.prefixe"
                       :placeholder="$t('store.form.prefixe')" />
              </div>

              <div class="modal-footer">
                <button type="button" class="btn btn-sm btn-light" data-bs-target="#editModal" data-bs-toggle="modal">
                  {{ $t('button.close') }}
                </button>
                <button type="submit" class="btn btn-sm  btn-primary" :disabled="isLoading">
                  <span class="indicator-label" v-if="!isLoading">{{ $t('button.save') }}</span>
                  <span v-else>
                    {{ $t('loading') }}
                    <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import AppSubViewHeader from "@/views/Shop/partials/AppSubViewHeader";
import ShopBranding from "@/views/Shop/Branding.vue";
import { VueTelInput } from 'vue-tel-input';
import 'vue-tel-input/dist/vue-tel-input.css';
import { mapActions, mapGetters } from "vuex";
import InputTel from "@/components/InputTel.vue";
import Loader from "@/components/Loader.vue";
import QRCodeVue3 from "qrcode-vue3";
export default {
  name: "View",
  components: {InputTel, AppSubViewHeader, VueTelInput, Loader, QRCodeVue3, ShopBranding },
  data() {
    return {
      title: i18n.global.t('breadcrumbs.shop.shops'),
      breadcrumbs: [
        {
          name: i18n.global.t('breadcrumbs.shop.shops'),
          path: '/admin/shops'
        },
        {
          name: i18n.global.t('breadcrumbs.shop.managment'),
          path: ''
        },

      ],
      shop: {
        stores: []
      },
      dialCode:'',
      phoneNumber:'',
      shopUsersCount: 0,
      shopProductCount: 0,
      shopProductEndedCount: 0,
      shopUsers: {},
      shopUnvalidateEntryCount: 0,
      shopNotConformEntryCount: 0,
      shopOrdersCount: 0,
      numberTransfertNoValidate: 0,
      shopEntryCount : 0,
      shopTransferCount : 0,
      formEditData: {
        id: null,
        name: '',
        invoice_number: '',
        phone: '',
        phoneCode: '',
        address: '',
        transfer_number : '',
        online: true,
        prefix: '',
        stores: []
      },
      formDeleteData: {
        id: null
      },
      isLoading: false,
      removeUser: false,
      shopActiveUsers: {},
      formStore: {
        id: '',
        name: '',
        address: '',
        phone: '',
        phoneCode: '',
        etat: 'active',
        prefixe: ''
      },
      status: "all",
      search: null,
      listShops: [],
      isReady: false,
      closedMessage: '',
      isToggling: false,
    }
  },
  computed: {
    /**
     * Menu de la fiche : 7 thèmes au lieu d'une entrée par page. Une page n'apparaît que si
     * l'utilisateur a le droit et la boutique le module ; un thème vide disparaît.
     * active : routes qui ouvrent l'onglet (sous-pages comprises).
     */
    // points rouges de cette boutique (chargés par le menu principal, voir shop/loadShopAlerts)
    shopAlert() {
      return this.$store.getters['shop/getShopAlerts'][this.$route.params.id] || {}
    },

    menuGroups() {
      const can = (permission) => this.$can(permission)
      const has = (module) => this.$hasShopModule(module)
      const groups = [
        { key: 'items', label: 'shop.menu_groups.items', icon: 'bi bi-box-seam', items: [
          { route: 'shop-products', label: 'shop.shop_items', show: can('product_boutique_view'), active: ['shop-detail-products', 'shop-products', 'shop-add-products'], badge: this.shopProductCount, badgeClass: 'badge-light-primary' },
          { route: 'shop-products-ended', label: 'shop.shop_ended_items', show: can('product_boutique_view') && has('pos.stock'), badge: this.shopProductEndedCount, badgeClass: 'badge-light-danger', alert: this.shopAlert.ended_products },
          { route: 'shop-products-stagnant', label: 'shop.low_rotation_products', show: can('product_boutique_view') && has('pos.stock') },
          { route: 'shop-barcode', label: 'shop.barcodes', show: can('product_boutique_view') && has('pos.stock') },
        ] },
        { key: 'stock', label: 'shop.menu_groups.stock', icon: 'bi bi-boxes', items: [
          { route: 'shop-entries', label: 'shop.entries', show: can('entry_boutique_view') && has('pos.stock'), active: ['shop-entries', 'shop-entries-unconform'] },
          { route: 'shop-moves', label: 'shop.menu_move', show: can('boutique_transfert_view') && has('pos.stock'), active: ['shop-moves', 'shop-create-output', 'shop-update-output', 'shop-store-output'], badge: this.numberTransfertNoValidate, badgeClass: 'badge-light-primary', alert: this.shopAlert.transfers_to_validate },
          { route: 'shop-inventories', label: 'inventory.menu', show: can('inventory_boutique_view') && has('pos.stock'), active: ['shop-inventories', 'shop-inventory-detail'] },
        ] },
        { key: 'sales', label: 'shop.menu_groups.sales', icon: 'bi bi-receipt', items: [
          { route: 'shop-orders', label: 'shop.sales', show: can('sale_boutique_view') && has('pos.sales'), active: ['shop-orders', 'order-details'] },
          { route: 'shop-credits', label: 'credit.menu', show: can('credit_view') && has('pos.sales'), alert: this.shopAlert.overdue_credits },
          { route: 'session-path', label: 'shop.cash_sessions', show: can('sale_boutique_view') && has('pos.sales'), active: ['session-path', 'session-details', 'session-details-default', 'session-sales-path', 'session-products-path'] },
        ] },
        { key: 'boxes', label: 'shop.box', icon: 'bi bi-cash-coin', items: [
          { route: 'shop-boxes', label: 'shop.box', show: can('box_view') && has('pos.cash_register') },
        ] },
        { key: 'marketing', label: 'shop.menu_groups.marketing', icon: 'bi bi-megaphone', items: [
          { route: 'shop-promotions', label: 'shop.shop_promotions', show: can('product_boutique_view') && has('ecommerce.promotions'), active: ['shop-promotions', 'shop-promotion-detail', 'shop-promotion-create'] },
          { route: 'shop-loyalty', label: 'shop.shop_loyalty', show: can('loyalty_program_view') && has('loyalty') },
        ] },
        { key: 'team', label: 'shop.agents', icon: 'bi bi-people', items: [
          { route: 'shop-users', label: 'shop.agents', show: can('user_boutique_view') },
        ] },
        { key: 'settings', label: 'shop.menu_groups.settings', icon: 'bi bi-gear', items: [
          { route: 'shop-media', label: 'setting.multimedia', show: has('ecommerce'), active: ['shop-media', 'shop-media-list'] },
          { route: 'shop-config-stores', label: 'shop.config_stores', show: can('boutique_store_config_view') && has('pos.warehouse') },
        ] },
      ]

      return groups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => item.show).map((item) => ({ ...item, active: item.active || [item.route] })),
        }))
        .filter((group) => group.items.length)
    },
    activeGroup() {
      return this.menuGroups.find((group) => group.items.some((item) => item.active.includes(this.$route.name))) || null
    },
    ...mapGetters('countries', [
      'getCountries'
    ]),
    ...mapGetters('store', ['getStores']),

    ecommerceUrl() {
      // Chaque boutique a son propre domaine (shops.domain, résolu par
      // EcommerceShopScope côté API) — un fallback global identique pour
      // toutes les boutiques était non seulement obsolète (mauvais port en
      // local) mais surtout faux dès qu'il y a plusieurs boutiques.
      if (this.shop?.domain) {
        return `https://${this.shop.domain}`
      }
      return (process.env.VUE_APP_ECOMMERCE_URL || 'https://front-shop.test:8095').replace(/\/$/, '')
    },
    ...mapGetters('shop', ['getShops']),



    options(){
      return {
        placeholder: i18n.global.t('enter_phone'),
        showDialCode : false
      }
    },
    dropdownOptions(){
      return {
        showDialCodeInSelection: true,
        showDialCodeInList: true,
        showFlags: true
      }
    }
  },

  watch: {
    '$route.params.id': {
      immediate: true,
      handler(id) {
        // Les écrans du détail agissent sur CETTE boutique (en-tête X-Managed-Shop, voir main.js)
        window.managedShopId = id || null
      },
    },
  },

  beforeUnmount() {
    window.managedShopId = null
  },

  async mounted() {
    this.isReady = false
    this.loadCountries()
    this.loadTaxes()
    await this.loadShopModules(this.$route.params.id)
    if (this.$hasShopModule('pos.warehouse')) {
      this.loadStore({status: "all", search: null});
    }
    if (this.$hasShopModule('pos.stock')) {
      this.getTransfertNumberNoValidadate()
    }
    this.initSelect()
    await this.initEditData()
    this.isReady = true
  },


  methods: {
    ...mapActions('tenantModule', ['loadShopModules']),
    ...mapActions('countries', [
      'loadCountries',
    ]),

    ...mapActions('shop', [
      'showShop',
      'updateShop',
      'deleteShop',
      'archiveShop',
      'unArchiveShop',
      'countNoValidateOutputs',
      'toggleShopOpen',
    ]),

    ...mapActions('store', ['loadStore', 'createStore']),

    ...mapActions('taxes', ['loadTaxes']),

    showNoConformEntryList() {
      this.$router.push('/admin/shops/' + this.$route.params.id + '/view/entries/no_conform')
    },

    // Sur mobile le menu est empilé au-dessus du contenu : après un clic on descend sur la page choisie
    scrollToContent(e) {
      if (!e.target.closest('.menu-link') || window.innerWidth >= 768) return
      const el = this.$refs.shopContent
      if (!el) return
      // La page choisie se charge après coup : on réserve un écran au contenu pour que
      // le navigateur ne bloque pas le scroll avant, et on scrolle sans animation
      // (en 'smooth' l'animation reste figée pendant le chargement de la page)
      el.style.minHeight = 'calc(100vh - 75px)'
      el.scrollIntoView({ behavior: 'instant', block: 'start' })
    },
    routePath(name) {
       return { name : name , params : { id :this.$route.params.id }}
    },
    async getTransfertNumberNoValidadate(){
      let response = await this.countNoValidateOutputs(this.$route.params.id)
      this.numberTransfertNoValidate = response.data
    },
     openEditModal() {
      this.isLoading = false;
      this.updateFormEditData(this.shop)
      this.$refs.branding?.load()
      $('#editModal').modal('toggle')

    },

    initSelect(){
      $("#editModal").on("shown.bs.modal", function () {
        $('.kt_select2').select2();
      });

      $(".kt_select2").change((e) => {
        switch (e.target.name) {
          case 'active':
            this.formEditData.archive = e.target.value == 'true' ? true : false
            break;
          case 'stores[]':
            let addStores = [];
            for (var k = 0, kLen = e.target.selectedOptions.length; k<kLen; k++) {
              if(e.target.selectedOptions[k].value != '' || e.target.selectedOptions[k].value != undefined){
                addStores.push(e.target.selectedOptions[k].value);
              }
            }
            this.formEditData.stores = addStores
            break;
        }
      });
    },

    async initEditData() {
      let response = await this.showShop(this.$route.params.id)
      this.shop = response.data.shop
      this.shopUsers = response.data.shopUsers
      this.shopActiveUsers = response.data.shopActiveUsers
      this.shopUsersCount = response.data.shopUsersCount
      this.shopProductCount = response.data.shopProductCount
      this.shopProductEndedCount = response.data.shopProductEndedCount ?? 0
      this.shopUnvalidateEntryCount = response.data.shopUnvalidateEntryCount
      this.shopNotConformEntryCount = response.data.shopNotConformEntryCount
      this.shopOrdersCount = response.data.shopOrdersCount
      this.shopEntryCount =  response.data.shopEntryCount ?? 0
      this.shopTransferCount =  response.data.shopTransferCount ?? 0
    },
    splitShopPhone(shop) {
      const phone = (shop.phone || '').trim()
      const legacy = phone.match(/^\+?(\d{1,4})\s+(.+)$/)
      if (legacy) {
        return { phoneCode: shop.phone_code || '+' + legacy[1], phone: legacy[2] }
      }
      return { phoneCode: shop.phone_code || '', phone }
    },

    async updateFormEditData(shop) {
      // this.phoneNumber=shop.phone.replace(shop.phone.split(" ")[0], '')
      return this.formEditData = {
        id: this.$route.params.id,
        name: shop.name,
        // indicatif et numéro séparés (phone_code / phone) ; ancien format « +229 0197... » encore géré
        ...this.splitShopPhone(shop),
        address: shop.address,
        invoice_number: shop.invoice_number,
        transfer_number: shop.transfer_number,
        online: shop.online,
        archive: shop.archive,
        prefix: shop.prefix,
        stores: shop?.stores?.map(el => el.id)
      }

      this.$nextTick(() => {
        this.initSelect()
      })
    },

    async updateShopData() {
      this.isLoading = true

      const data = {
        ...this.formEditData,
        phone: (this.formEditData.phone || '').trim(),
        phone_code: '+' + (this.formEditData.phoneCode || '').replace('+', ''),
      }

      let response = {}
       try {
        response = await this.updateShop(data)
        this.shop = response.data.shop
        // Coordonnées et identité, enregistrées avec la boutique
        await this.$refs.branding?.save({ silent: true })
        this.isLoading = false
        window.toastr['success'](i18n.global.t("shop.shop_updated"))

        this.$router.push('/admin/shops/' + this.$route.params.id + '/view')


        $('#editModal').modal('toggle')
        await this.updateFormEditData(res.data.shop)
        return true
      } catch (err) {
        this.isLoading = false;
      }
    },

    async deleteData() {
      this.isLoading = true
      let response = {}
      Swal.fire({
        title: i18n.global.t("notification.confirm_delete_shop"),
        text: i18n.global.t("notification.irreversible_action"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: i18n.global.t("shop.yes"),
        cancelButtonText: i18n.global.t("shop.no"),
      }).then(async (result) => {
        if (result.value) {
          try {
            this.formDeleteData.id = this.$route.params.id
            response = await this.deleteShop(this.formDeleteData)
            window.toastr['success'](i18n.global.t("shop.shop_deleted"))
            this.isLoading = false
            this.$router.push('/admin/shops')
          } catch (e) {
            window.toastr['error'](e)
            this.isLoading = false
          }
        }
      });
    },

    async unArchiveData() {
      this.isLoading = true
      let response = {}
      Swal.fire({
        title: i18n.global.t("notification.confirm_unarchive_shop"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: i18n.global.t("shop.yes"),
        cancelButtonText: i18n.global.t("shop.no"),
      }).then(async (result) => {
        if (result.value) {
          try {
            response = await this.unArchiveShop(this.$route.params.id)
            window.toastr['success'](i18n.global.t("shop.shop_unarchive"))
            this.isLoading = false
            let res = await this.showShop(this.$route.params.id)
            this.shop = res.data.shop
            this.shopUsersCount = res.data.shopUsersCount
            await this.updateFormEditData(res.data.shop)
            this.$router.push('/admin/shops/' + this.$route.params.id + '/view')
            this.isLoading = false
          } catch (e) {
            this.isLoading = false
          }
        }
      });
    },

    async archiveData() {
      this.isLoading = true
      let response
      Swal.fire({
        title: i18n.global.t("notification.confirm_archive_shop"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: i18n.global.t("shop.yes"),
        cancelButtonText: i18n.global.t("shop.no"),
      }).then(async (result) => {
        if (result.value) {
          try {
            this.isLoading = false
            response = await this.archiveShop(this.$route.params.id)
            window.toastr['success'](i18n.global.t("shop.shop_archive_notif"))
            let res = await this.showShop(this.$route.params.id)
            this.shop = res.data.shop
            await this.updateFormEditData(res.data.shop)
            this.$router.push('/admin/shops')
          } catch (err) {
            this.isLoading = false;
          }
        }
      });
    },

    thirstInputCountryChanged(country) {
        //let phoneNumber = this.formEditData.phone.replace(this.formEditData.phone.split(" ")[0], '');
        this.formEditData.phone ='+'+country.dialCode+' '+this.phoneNumber;
        this.dialCode ='+'+country.dialCode
    },

    thirstInputChanged(number, phoneObject) {
      let errorDiv = document.querySelector('[data-field="shop_phone"][data-field="shop_phone"][data-validator="callback"]')
      if (errorDiv) {
        errorDiv.remove()
      }

      if (phoneObject) {
        this.formEditData.phone = phoneObject.nationalNumber
        //this.formEditData.phone = '+' + phoneObject.countryCallingCode
      }
    },

    openToggleOpenModal() {
      if (this.shop.is_open !== false) {
        this.closedMessage = this.shop.closed_message || ''
        $('#toggleOpenModal').modal('show')
      } else {
        this.confirmToggleOpen()
      }
    },

    async confirmToggleOpen() {
      this.isToggling = true
      try {
        const res = await this.toggleShopOpen({
          id: this.$route.params.id,
          closed_message: this.shop.is_open !== false ? this.closedMessage : null,
        })
        this.shop.is_open = res.data.is_open
        this.shop.closed_message = res.data.closed_message
        $('#toggleOpenModal').modal('hide')
        const msg = res.data.is_open ? i18n.global.t('shop.shop_opened_notif') : i18n.global.t('shop.shop_closed_notif')
        window.toastr['success'](msg)
      } catch (e) {
        window.toastr['error'](i18n.global.t('main.error_try'))
      } finally {
        this.isToggling = false
      }
    },

    async createStoreData() {
      let response = {};
      this.isLoading = true;
      try {
        response = await this.createStore(this.formStore);
        $('#createStoreModal').modal('toggle')
        this.openEditModal()
        window.toastr['success'](i18n.global.t('notification.store.created'))
        this.isLoading = false;
        this.formStore.name = "";
        this.formStore.address = "";
        this.formStore.phone = "";
        this.formStore.prefixe = "";
      } catch (err) {
        this.isLoading = false;
      }
    }
  }
}
</script>

<style scoped>
/* Point rouge : ruptures, transferts à valider, crédits en retard (comme le menu E-commerce) */
.shop-alert-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f1416c;
  vertical-align: middle;
  animation: shop-alert-blink 1.1s ease-in-out infinite;
}
.shop-alert-dot--corner {
  position: absolute;
  top: -2px;
  right: -2px;
  border: 2px solid #fff;
  width: 10px;
  height: 10px;
}
@keyframes shop-alert-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.25; }
}
/* laisse la place de l'en-tête fixe quand on défile vers le contenu (mobile) */
.shop-content {
  scroll-margin-top: 75px;
}

.shop-menu-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  margin-right: 10px;
  font-size: 0.95rem;
  flex-shrink: 0;
  transition: all 0.2s ease;
}
/* Icônes : une seule couleur (celle de la boutique), comme le menu principal ;
   les classes icon-* restent sur les entrées mais ne changent plus la couleur */
.shop-menu-icon.icon-primary,
.shop-menu-icon.icon-success,
.shop-menu-icon.icon-warning,
.shop-menu-icon.icon-danger,
.shop-menu-icon.icon-info,
.shop-menu-icon.icon-dark {
  background: rgba(var(--kt-primary-rgb), 0.12);
  color: var(--kt-primary);
}

/* le thème grise les <i> du menu : l'icône prend la couleur de son cadre */
.shop-menu-icon i {
  color: inherit !important;
}

  .bdg {
    display: inline-block;
    padding: .325rem .5rem;
    font-size: .85rem;
    font-weight: 600;
    line-height: 1;
    color: #fff;
    text-align: center;
    white-space: nowrap;
    vertical-align: baseline;
    border-radius: .325rem;
  }

  .bdg.bdg-circle{
    border-radius: 50%;
    padding: 0;
    min-width: unset;
    width: 1.75rem;
  }

  .bdg.bdg-circle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 1.75rem;
    min-width: 1.75rem;
    padding: 0 .1rem;
    line-height: 0;
  }
</style>
